/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
	CircularProgress,
	Chip,
	Box,
	ListItemText,
	Typography,
	ListItemIcon,
	Autocomplete,
	TextField,
	Tooltip,
	Stack,
	ClickAwayListener,
	useTheme,
} from '@mui/material';
import { minimalInputTheme } from '@/app/theme';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery, useGetBrokerGroupUsersQuery } from '@/features/user/userApi';
import BrokerAvatar from '@/components/BrokerAvatar';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useChipFit } from '@/hooks/useChipFit';

// Thin wrapper around OwnerAutocomplete for callers that only have an ambiguous
// id (hubspotId or Firestore doc id) rather than the doc id OwnerAutocomplete
// needs for display. Resolves that id to a user first, then hands
// OwnerAutocomplete the doc id. `onChange` is passed straight through — the
// caller gets whatever OwnerAutocomplete emits (the full user object, or null).
export const OwnerSelect = ({
	initialId,
	disabled,
	onChange,
	minimal,
	allowRemoval = false,
	hideAvatar = false,
	showGroup = false,
	filterBy,
}) => {
	// getUserDetails' `userId` param resolves a doc id first and falls back to a
	// hubspotId, so this works regardless of which shape `initialId` is.
	const { data: initialOwner } = useGetUserDetailsQuery(
		{ userId: initialId },
		{ skip: !initialId },
	);

	// getBrokerGroupUsers entries key off id/docId (the Firestore doc id);
	// getUserDetails returns the same person keyed off userId — reshape so
	// OwnerAutocomplete can show them even if they're outside the fetched group.
	const fallbackUser = initialOwner
		? { ...initialOwner, id: initialOwner.userId, docId: initialOwner.userId }
		: null;

	return (
		<OwnerAutocomplete
			userId={initialOwner?.userId}
			disabled={disabled}
			minimal={minimal}
			showGroup={showGroup}
			hideAvatar={hideAvatar}
			disableClearable={!allowRemoval}
			onChange={onChange}
			fallbackUser={fallbackUser}
			{...(filterBy && { filterBy })}
		/>
	);
};

OwnerSelect.displayName = 'OwnerSelect';

const RETURN_VALUES = ['id', 'object'];

export const OwnerMultiSelect = ({
	users = [], // This is an array of Firebase document ids or user objects
	disabled,
	onChange,
	minimal,
	hideAvatar = false,
	showGroup = true,
	isUserDisabled = (user) => false, // User that cannot be selected
	isUserFixed = (user) => false, // User that cannot be removed
	returnValue = 'id', // Should be id or object for full object
	disableClearable = false,
	availableUsers,
	...props
}) => {
	const theme = useTheme();
	if (
		process.env.NODE_ENV === 'development' &&
		window.location.hostname === 'localhost' &&
		!RETURN_VALUES.includes(returnValue)
	) {
		throw new Error(
			'`returnValue` is required for <OwnerMultiSelect /> to function properly and must be either `id` or `object`.',
		);
	}

	const [open, setOpen] = useState(false);

	const containerRef = useRef();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });

	const brokerGroupUsersQuery = useGetBrokerGroupUsersQuery(
		{ userId: userDetails?.userId, showGroup },
		{
			skip: !userDetails?.userId,
		},
	);

	const brokerGroupUsers = (availableUsers ?? brokerGroupUsersQuery.data ?? []).reduce(
		(acc, current) => {
			if (!acc.find((item) => item?.[returnValue] === current?.[returnValue])) {
				acc.push(current);
			}
			return acc;
		},
		[],
	);

	const selected = brokerGroupUsers.filter((item) => {
		if (returnValue !== 'object') {
			return users.includes(item?.[returnValue]);
		} else {
			return users.map((u) => u?.id).includes(item?.id);
		}
	});

	const limitTags = useChipFit(containerRef, selected.length);

	const selectedNames = useMemo(() => {
		return selected.map((o) => o.name ?? o.email).join(', ');
	}, [selected]);

	const allOwnerIdsSelected = useMemo(() => {
		return brokerGroupUsers.every((o) => selected.find((i) => i.id === o.id));
	}, [selected, brokerGroupUsers]);

	const options = useMemo(() => {
		const removable = selected.filter((o) => !isUserFixed(o));
		const data = brokerGroupUsers
			.slice()
			.sort((a, b) => (a.name ?? a.email ?? a.id).localeCompare(b.name ?? b.email ?? b.id));
		return [
			...(!allOwnerIdsSelected ? [{ id: 'all', name: 'All' }] : []),
			...(removable.length > 0 ? [{ id: 'none', name: 'Clear Filters' }] : []),
			...data,
		];
	}, [brokerGroupUsers, allOwnerIdsSelected, selected]);

	return (
		<>
			<ClickAwayListener onClickAway={(e) => setOpen(false)}>
				<Box sx={{ position: 'relative' }}>
					<Autocomplete
						ref={containerRef}
						open={open}
						onOpen={(e) => setOpen(true)}
						onClose={(e, reason) => {
							if (!['selectOption', 'removeOption'].includes(reason)) {
								setOpen(false);
							}
						}}
						fullWidth
						disabled={
							disabled ||
							(availableUsers == null &&
								(!brokerGroupUsersQuery.isSuccess || !userQuery.isSuccess))
						}
						isOptionEqualToValue={(option, val) =>
							option?.id === val?.id || option?.email === val?.email
						}
						getOptionLabel={(option) => option?.name ?? option?.email ?? ''}
						options={options}
						// groupBy={(option) => option?.brokerGroupId}
						loading={
							availableUsers == null &&
							(!brokerGroupUsersQuery.isSuccess || !userQuery.isSuccess)
						}
						value={selected}
						multiple
						disableClearable={disableClearable}
						onChange={(_, v) => {
							const newVals = [...(v ?? [])].filter(
								(i) => !['none', 'all'].includes(i.id),
							);

							const fixedOptions = newVals.filter((option) => isUserFixed(option));
							let newValues = fixedOptions.slice();
							if (v?.some((i) => i.id === 'none')) {
								newValues = fixedOptions.slice();
								// Select none except fixedOptions
							} else if (v?.some((i) => i.id === 'all')) {
								// Select all except disabled options
								newValues = brokerGroupUsers.filter((o) => !isUserDisabled(o));
							} else {
								newValues = [...newVals, ...fixedOptions];
							}
							newValues = newValues
								// .map((i) => i?.id)
								.reduce((acc, current) => {
									if (!acc.find((item) => item?.id === current?.id)) {
										acc.push(current);
									}
									return acc;
								}, [])
								.map((i) => {
									if (returnValue === 'object') return i;
									return i[returnValue];
								});
							onChange(newValues);
						}}
						getOptionDisabled={(option) => {
							return isUserDisabled(option) || isUserFixed(option);
						}}
						renderOption={(props, option) => {
							const isHelperOption = ['all', 'none'].includes(option.id);
							const checked =
								selected.some((s) => s.id === option.id) ||
								(option.id === 'all' && allOwnerIdsSelected);
							const Icon = checked ? CheckBox : CheckBoxOutlineBlank;
							return (
								<li
									{...props}
									style={{ width: '100%' }}
									key={`${option.id}-${props.key}`}
								>
									<ListItemIcon sx={{ minWidth: '2.25em' }}>
										<Icon
											{...(checked && {
												sx: { color: theme.palette.primary.main },
											})}
										/>
									</ListItemIcon>
									{!hideAvatar && !isHelperOption && (
										<ListItemIcon sx={{ minWidth: '2.5em' }}>
											<BrokerAvatar broker={option} />
										</ListItemIcon>
									)}
									<Typography>{option?.name ?? option?.email}</Typography>
								</li>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								onKeyDown={(e) => {
									e.preventDefault();
								}}
								slotProps={{
									input: {
										...params.InputProps,
										readOnly: true,

										style: {
											caretColor: 'transparent',
											cursor: 'pointer',
										},
										endAdornment: (
											<React.Fragment>
												{brokerGroupUsersQuery.isLoading ? (
													<CircularProgress color='inherit' size={20} />
												) : null}
												{params.InputProps.endAdornment}
											</React.Fragment>
										),

										cursor: 'pointer',
										onBlur: (e) => console.log('on blur', e),
									},
								}}
								sx={{
									cursor: 'pointer',
									...(users.length > 0 && {
										'& input': { display: 'none' },
									}),
								}}
							/>
						)}
						limitTags={limitTags}
						renderValue={(value, getItemProps) =>
							value.map((option, index) => {
								const { key, ...itemProps } = getItemProps({ index });
								return (
									<Chip
										key={key}
										size='small'
										label={option.name ?? option.email ?? ''}
										{...itemProps}
										{...(!hideAvatar && {
											avatar: <BrokerAvatar broker={option} />,
										})}
										disabled={isUserFixed(option)}
									/>
								);
							})
						}
						{...(props ?? {})}
						sx={{
							cursor: 'pointer',
							...(minimal && {
								'& .MuiInput-underline:before': {
									borderBottomColor: theme.palette.divider,
								},
								'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
									borderBottomColor: theme.palette.divider,
								},
							}),
							'& .MuiInputBase-root': {
								cursor: 'pointer',
								py: 0,
								flexWrap: 'nowrap', // prevents wrapping
								overflow: 'hidden',
							},
							'& .MuiAutocomplete-tag': {
								maxWidth: '100%',
							},
							'& input': {
								cursor: 'pointer',
							},
							...(props?.sx ?? {}),
						}}
					/>
				</Box>
			</ClickAwayListener>
		</>
	);
};

OwnerMultiSelect.displayName = 'OwnerMultiSelect';

const CHIP_WIDTH = 120;
const RESERVED_SPACE = 48;

const RenderOption = ({ option, hideAvatar, displayField }) => {
	const showAdditionalField = displayField && option?.[displayField];
	return (
		<Stack direction='row' width='100%' alignItems='center'>
			{!hideAvatar && (
				<ListItemIcon sx={{ minWidth: '3em' }}>
					<BrokerAvatar broker={option} />
				</ListItemIcon>
			)}
			<Stack
				direction='row'
				width='100%'
				alignItems='center'
				{...(showAdditionalField && { justifyContent: 'space-between' })}
			>
				<Typography
					sx={{
						flex: 1,
						minWidth: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{option?.name ?? option?.email}
				</Typography>
				{showAdditionalField && (
					<Typography
						variant='task_updated'
						sx={{
							flexShrink: 0,
							whiteSpace: 'nowrap',
						}}
					>
						{option[displayField]}
					</Typography>
				)}
			</Stack>
		</Stack>
	);
};

RenderOption.displayName = 'RenderOption';

export const OwnerAutocomplete = ({
	userId, // This is either a Firebase document id or an array of Firebase document ids - it is expected to be an array when multiple is true
	disabled,
	onChange,
	minimal,
	hideAvatar = false,
	showGroup = true,
	multiple = false,
	isUserDisabled = (user) => false, // User that cannot be selected
	isUserFixed = (user) => false, // User that cannot be removed
	disableClearable = false,
	filterBy = (user) => true, // Users to be filtered out
	onBlur = () => {},
	error = false,
	displayField,
	// The currently-assigned owner, shown even if they don't appear in the
	// fetched broker group (e.g. they've left the sales team or the group) —
	// still selectable-away-from, just not re-selectable.
	fallbackUser,
	...props
}) => {
	const theme = useTheme();
	const containerRef = useRef();
	const [limitTags, setLimitTags] = useState(-1);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });

	const brokerGroupUsersQuery = useGetBrokerGroupUsersQuery(
		{ userId: userDetails?.userId, showGroup },
		{
			skip: !userDetails?.userId,
		},
	);

	const brokerGroupUsers = useMemo(() => {
		const data = (brokerGroupUsersQuery.data ?? []).reduce((acc, current) => {
			if (!acc.find((item) => item.id === current.id) && filterBy(current)) {
				acc.push(current);
			}
			return acc;
		}, []);
		// Sort users alphabetically A-Z
		const sorted = data.sort((a, b) =>
			(a?.name ?? a?.email ?? '').localeCompare(b?.name ?? b?.email ?? ''),
		);
		if (fallbackUser?.id && !sorted.some((user) => user.id === fallbackUser.id)) {
			return [...sorted, fallbackUser];
		}
		return sorted;
	}, [brokerGroupUsersQuery.data, filterBy, fallbackUser]);

	const selected = multiple
		? brokerGroupUsers.filter((item) => {
				const vals = userId ?? [];
				return vals.includes(item?.id) || vals.includes(item?.docId);
			})
		: brokerGroupUsers.find((item) => {
				return (
					userId === item?.id ||
					userId === item?.docId ||
					(userId &&
						Array.isArray(userId) &&
						(userId.includes(item?.id) || userId.includes(item?.docId)))
				);
			});

	useLayoutEffect(() => {
		if (!multiple || !containerRef.current) return;

		const containerWidth = containerRef.current.offsetWidth;

		const available = containerWidth - RESERVED_SPACE;
		const maxChips = Math.floor(available / CHIP_WIDTH);

		setLimitTags(maxChips > 0 ? maxChips : 1);
	}, [userId, multiple]);

	return (
		<Autocomplete
			ref={containerRef}
			fullWidth
			onBlur={onBlur}
			disabled={disabled || !brokerGroupUsersQuery.isSuccess || !userQuery.isSuccess}
			isOptionEqualToValue={(option, val) =>
				option?.id === val?.id ||
				option?.docId === val?.docId ||
				option?.email === val?.email
			}
			getOptionLabel={(option) => option?.name ?? option?.email ?? ''}
			options={brokerGroupUsers}
			loading={!brokerGroupUsersQuery.isSuccess || !userQuery.isSuccess}
			value={selected ?? null}
			multiple={multiple}
			disableClearable={disableClearable}
			onChange={(_, v) => {
				if (multiple) {
					const fixedOptions = selected.filter((option) => isUserFixed(option));
					const newValues = [...(v ?? []), ...fixedOptions]
						.map((i) => i?.id)
						.reduce((acc, current) => {
							if (!acc.find((item) => item === current)) {
								acc.push(current);
							}
							return acc;
						}, []);
					onChange(newValues);
				} else {
					onChange(v ?? null);
				}
			}}
			getOptionDisabled={(option) => {
				return (
					isUserDisabled(option) ||
					(multiple && isUserFixed(option)) ||
					option?.id === fallbackUser?.id
				);
			}}
			renderOption={(props, option) => (
				<li {...props} style={{ width: '100%' }} key={`${option.id}-${props.key}`}>
					<RenderOption {...{ option, hideAvatar, displayField }} />
				</li>
			)}
			limitTags={limitTags}
			renderInput={(params) => (
				<TextField
					{...params}
					variant={minimal ? 'standard' : 'outlined'}
					error={error}
					onKeyDown={(e) => {
						e.preventDefault();
					}}
					slotProps={{
						input: {
							...params.InputProps,
							readOnly: true,

							style: {
								caretColor: 'transparent',
								cursor: 'pointer',
							},
							endAdornment: (
								<React.Fragment>
									{brokerGroupUsersQuery.isLoading ? (
										<CircularProgress color='inherit' size={20} />
									) : null}
									{params.InputProps.endAdornment}
								</React.Fragment>
							),

							cursor: 'pointer',
						},
					}}
					sx={{
						cursor: 'pointer',
						...(multiple && userId?.length > 0 && { '& input': { display: 'none' } }),
						...(minimal && {
							...minimalInputTheme,
							'& .MuiInputBase-root': {
								backgroundColor: 'transparent',
							},
						}),
					}}
				/>
			)}
			renderValue={(value, getItemProps) => {
				if (multiple) {
					return value.map((option, index) => {
						const { key, ...itemProps } = getItemProps({ index });
						return (
							<Tooltip
								title={`${option.name ?? option.email ?? ''}${displayField && option[displayField] ? ` (${option[displayField]})` : ''}`}
								key={key}
							>
								<div>
									<Chip
										label={
											userId.length > 1 && !hideAvatar
												? ''
												: (option.name ?? option.email ?? '')
										}
										{...itemProps}
										{...(!hideAvatar && {
											avatar: <BrokerAvatar broker={option} />,
										})}
										disabled={isUserFixed(option)}
									/>
								</div>
							</Tooltip>
						);
					});
				}
				// Let clicks pass through to the input root so clicking the value opens the dropdown
				return (
					<Box sx={{ display: 'contents', pointerEvents: 'none' }}>
						<RenderOption {...{ option: value, hideAvatar, displayField }} />
					</Box>
				);
			}}
			{...(props ?? {})}
			sx={{
				cursor: 'pointer',
				'& .MuiInputBase-root': {
					cursor: 'pointer',
					...(!multiple && { paddingRight: '9px !important' }),
				},
				'& input': {
					cursor: 'pointer',
				},
				...(props?.sx ?? {}),
			}}
		/>
	);
};

OwnerAutocomplete.displayName = 'OwnerAutocomplete';
